import { useState } from 'react';
import { motion } from 'motion/react';
import { GitCompareArrows } from 'lucide-react';
import type { AnalogLens, StockAnalogLensData } from '@/mocks/stockAnalogMock';
import type { SectorAnalysisPayload } from '@/services/api';
import { formatPct, LensAccordion, pctChipClass } from './AnalogLensAccordion';

type Props = {
  analysis: SectorAnalysisPayload;
  className?: string;
};

const SECTOR_LOADING_COPY = {
  situational: {
    collapsed: 'Loading catalyst-theme analogs…',
    expanded: 'Loading situational SOX catalyst analogs…',
    empty: 'No situational catalyst matches for this session.',
  },
  mechanical: {
    collapsed: 'Matching SOX price fingerprint…',
    expanded: 'Scanning SOX OHLC history for price-shape matches…',
    empty: 'Not enough SOX history to form mechanical analogs.',
  },
  macro: {
    collapsed: 'Loading macro / regime analogs…',
    expanded: 'Loading market macro / regime backdrop analogs…',
    empty: 'No macro / regime matches for this session.',
  },
};

function resolveLenses(analysis: SectorAnalysisPayload): StockAnalogLensData[] {
  if (analysis.lenses?.length) {
    return analysis.lenses;
  }
  if (analysis.lens) {
    return [analysis.lens];
  }
  return [];
}

export function SectorAnalogAnalysisPanel({ analysis, className = '' }: Props) {
  const lenses = resolveLenses(analysis);
  const [openLens, setOpenLens] = useState<AnalogLens | null>(null);
  const [expandedMatchDate, setExpandedMatchDate] = useState<string | null>(null);

  const handleLensToggle = (lensId: AnalogLens) => {
    setOpenLens((prev) => {
      if (prev === lensId) {
        setExpandedMatchDate(null);
        return null;
      }
      setExpandedMatchDate(null);
      return lensId;
    });
  };

  const handleMatchToggle = (date: string) => {
    setExpandedMatchDate((prev) => (prev === date ? null : date));
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className={`overflow-hidden rounded-3xl border border-gray-200 bg-white ${className}`}
    >
      <div className="border-b border-gray-100 px-6 py-5 md:px-8 md:py-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-900">
                <GitCompareArrows className="h-4 w-4 text-white" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                Historical Analogues
              </span>
            </div>
            <h3 className="text-xl font-semibold tracking-tight text-gray-900">
              {analysis.reference_date_label} — {analysis.proxy_label}
            </h3>
            {analysis.summary ? (
              <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-gray-500">{analysis.summary}</p>
            ) : (
              <p className="mt-1 text-sm text-gray-500">SOX semiconductor index</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <span
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${pctChipClass(analysis.percent_change)} bg-gray-50`}
            >
              {formatPct(analysis.percent_change)}
            </span>
            {analysis.close != null ? (
              <span className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm text-gray-700">
                ${analysis.close.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="space-y-2 px-4 py-4 md:px-6 md:py-5">
        {lenses.map((lens) => (
          <LensAccordion
            key={lens.id}
            lens={lens}
            isOpen={openLens === lens.id}
            onToggle={() => handleLensToggle(lens.id)}
            expandedMatchDate={openLens === lens.id ? expandedMatchDate : null}
            onToggleMatch={handleMatchToggle}
            loadingCopy={SECTOR_LOADING_COPY}
            referenceDate={analysis.reference_date}
          />
        ))}
      </div>
    </motion.section>
  );
}
